<template>
  <div
    class="user-chip-multi-select"
    :class="{
      'user-chip-multi-select--assignment-dialog': variant === 'assignment-dialog',
      'user-chip-multi-select--combobox': isCombobox,
      'user-chip-multi-select--add-task': variant === 'add-task',
    }"
    :data-testid="dataTestId"
  >
    <div
      v-if="label"
      class="user-chip-multi-select__label"
      :class="{ 'user-chip-multi-select__label--required': required }"
    >
      <span>{{ label }}</span>
      <v-icon
        v-if="variant === 'assignment-dialog'"
        class="user-chip-multi-select__search-icon"
        small
      >
        mdi-magnify
      </v-icon>
    </div>

    <!-- Add Task: Figma checkbox dropdown with search + Clear/Add footer -->
    <div
      v-if="variant === 'add-task'"
      ref="addTaskMenuRoot"
      class="user-chip-multi-select__add-task-root"
    >
    <v-menu
      ref="addTaskMenu"
      v-model="addTaskMenuOpen"
      :close-on-content-click="false"
      :disabled="loading"
      offset-y
      fixed
      :attach="false"
      :z-index="10000"
      content-class="user-chip-multi-select__add-task-menu"
      :data-testid="`${dataTestId}__menu`"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          ref="addTaskActivator"
          class="user-chip-multi-select__activator"
          :class="{ 'user-chip-multi-select__activator--empty': !selectedUsers.length }"
          v-bind="attrs"
          :data-testid="`${dataTestId}__search`"
          @click="onAddTaskActivatorClick($event, on)"
        >
          <div class="user-chip-multi-select__activator-content">
            <template v-if="selectedUsers.length">
              <v-chip
                v-for="(user, index) in selectedUsers"
                :key="String(user._id)"
                close
                small
                label
                class="user-chip-multi-select__chip ma-0"
                :data-testid="getChipTestId(user, index)"
                @mousedown.stop
                @click.stop
                @click:close="removeUser(user._id)"
              >
                <v-avatar left size="24" class="user-chip-multi-select__avatar white--text">
                  <span class="user-chip-multi-select__avatar-text">{{ userInitial(user) }}</span>
                </v-avatar>
                <span>{{ user.name }}</span>
              </v-chip>
            </template>
            <span
              v-else
              class="user-chip-multi-select__activator-placeholder"
            >{{ placeholder }}</span>
          </div>
          <v-progress-circular
            v-if="loading"
            class="user-chip-multi-select__activator-chevron"
            indeterminate
            size="16"
            width="2"
          />
          <v-icon
            v-else
            class="user-chip-multi-select__activator-chevron"
            small
            @click.stop="onAddTaskActivatorClick($event, on)"
          >
            mdi-chevron-down
          </v-icon>
        </div>
      </template>

      <div class="user-chip-multi-select__add-task-panel">
        <div class="user-chip-multi-select__add-task-search">
          <v-text-field
            ref="addTaskMenuSearch"
            v-model="menuSearchQuery"
            dense
            outlined
            hide-details
            placeholder="Search"
            append-icon="mdi-magnify"
            data-testid="user-chip-multi-select__menu-search"
            @click.stop
            @mousedown.stop
            @keydown.stop
          />
        </div>

        <div
          class="user-chip-multi-select__add-task-list"
          role="listbox"
          :aria-multiselectable="true"
        >
          <p
            v-if="!menuUsers.length"
            class="user-chip-multi-select__add-task-empty"
            data-testid="user-chip-multi-select__add-task-empty"
          >
            {{ emptyMessage }}
          </p>

          <template v-else>
          <button
            type="button"
            class="user-chip-multi-select__add-task-row user-chip-multi-select__add-task-row--all"
            data-testid="user-chip-multi-select__menu-item--all"
            @click="toggleAllMenuUsers"
          >
            <v-checkbox
              :input-value="isAllMenuUsersSelected"
              :indeterminate="isAllMenuUsersIndeterminate"
              color="primary"
              hide-details
              dense
              class="user-chip-multi-select__add-task-checkbox"
              readonly
              @click.stop="toggleAllMenuUsers"
            />
            <span class="user-chip-multi-select__add-task-row-label user-chip-multi-select__add-task-row-label--all">All</span>
          </button>

          <button
            v-for="(user, index) in menuUsers"
            :key="String(user._id)"
            type="button"
            role="option"
            class="user-chip-multi-select__add-task-row"
            :class="{ 'user-chip-multi-select__add-task-row--selected': isUserSelected(user._id) }"
            :data-testid="getMenuItemTestId(user, index)"
            @click="toggleUser(user._id)"
          >
            <v-checkbox
              :input-value="isUserSelected(user._id)"
              color="primary"
              hide-details
              dense
              class="user-chip-multi-select__add-task-checkbox"
              readonly
              @click.stop="toggleUser(user._id)"
            />
            <span class="user-chip-multi-select__add-task-row-label">{{ user.name }}</span>
          </button>
          </template>
        </div>

        <div class="user-chip-multi-select__add-task-footer">
          <v-btn
            class="secondaryBtn user-chip-multi-select__add-task-clear-btn"
            data-testid="user-chip-multi-select__menu-clear"
            @click="clearAllUsers"
          >
            Clear
          </v-btn>
          <v-btn
            class="primary user-chip-multi-select__add-task-add-btn"
            data-testid="user-chip-multi-select__menu-add"
            @click="closeAddTaskMenu"
          >
            Add
          </v-btn>
        </div>
      </div>
    </v-menu>
    </div>

    <!-- Combobox: chips + search share one field (no layout jump) -->
    <v-autocomplete
      v-else-if="isCombobox"
      ref="menuField"
      :value="selectedUsers"
      :items="filteredUsers"
      item-text="name"
      return-object
      multiple
      hide-selected
      dense
      outlined
      hide-details
      :attach="attach"
      :disabled="loading"
      :search-input.sync="searchQuery"
      :placeholder="comboboxPlaceholder"
      append-icon="mdi-chevron-down"
      :menu-props="comboboxMenuProps"
      class="user-chip-multi-select__combobox"
      :class="[
        comboboxFieldClass,
        { 'user-chip-multi-select__combobox--empty': isComboboxEmpty },
      ]"
      :data-testid="`${dataTestId}__search`"
      @input="onComboboxInput"
    >
      <template v-slot:selection="{ attrs, item, parent, selected }">
        <v-chip
          v-bind="attrs"
          :input-value="selected"
          close
          small
          label
          class="user-chip-multi-select__chip ma-0"
          :data-testid="getChipTestId(item)"
          @click:close="parent.selectItem(item)"
        >
          <v-avatar left size="24" class="user-chip-multi-select__avatar white--text">
            <span class="user-chip-multi-select__avatar-text">{{ userInitial(item) }}</span>
          </v-avatar>
          <span>{{ item.name }}</span>
        </v-chip>
      </template>
      <template v-slot:item="{ item }">
        <v-list-item-avatar size="28" class="white--text">
          <span class="text-uppercase font-size-xs">{{ userInitial(item) }}</span>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ item.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>

    <template v-else>
      <v-autocomplete
        v-if="variant === 'assignment-dialog'"
        :key="`dialog-search-${selectionResetKey}`"
        :value="null"
        :items="filteredUsers"
        item-text="name"
        item-value="_id"
        :search-input.sync="searchQuery"
        :placeholder="placeholder"
        dense
        hide-details
        clearable
        outlined
        :attach="attach"
        :disabled="loading"
        class="user-chip-multi-select__autocomplete user-chip-multi-select__autocomplete--dialog"
        :data-testid="`${dataTestId}__search`"
        @change="onUserSelected"
      >
        <!-- No chevron on this variant; spinner in append-outer keeps loading visible without changing selection. -->
        <template v-slot:append-outer>
          <v-progress-circular
            v-if="loading"
            class="user-chip-multi-select__dialog-loading"
            indeterminate
            size="16"
            width="2"
            data-testid="user-chip-multi-select__loading"
          />
        </template>
        <template v-slot:item="{ item }">
          <v-list-item-avatar size="28" class="white--text">
            <span class="text-uppercase font-size-xs">{{ userInitial(item) }}</span>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>

      <p
        v-if="currentUsersLabel && selectedUsers.length"
        class="user-chip-multi-select__current-label"
      >
        {{ currentUsersLabel }}
      </p>

      <div
        v-if="selectedUsers.length"
        class="user-chip-multi-select__chips d-flex flex-wrap"
        :data-testid="`${dataTestId}__chips`"
      >
        <v-chip
          v-for="(user, index) in selectedUsers"
          :key="String(user._id)"
          close
          small
          label
          class="user-chip-multi-select__chip ma-1"
          :data-testid="getChipTestId(user, index)"
          @click:close="removeUser(user._id)"
        >
          <v-avatar left size="24" class="user-chip-multi-select__avatar white--text">
            <span class="text-uppercase font-size-xs">{{ userInitial(user) }}</span>
          </v-avatar>
          <span>{{ user.name }}</span>
        </v-chip>
      </div>

      <v-autocomplete
        v-if="variant !== 'assignment-dialog'"
        :key="`default-search-${selectionResetKey}`"
        :value="null"
        :items="filteredUsers"
        item-text="name"
        item-value="_id"
        :search-input.sync="searchQuery"
        :placeholder="placeholder"
        dense
        hide-details
        clearable
        :attach="attach"
        :disabled="loading"
        class="user-chip-multi-select__autocomplete mt-1"
        :data-testid="`${dataTestId}__search`"
        @change="onUserSelected"
      >
        <template v-slot:item="{ item }">
          <v-list-item-avatar size="28" class="white--text">
            <span class="text-uppercase font-size-xs">{{ userInitial(item) }}</span>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </template>
    <p
      v-if="duplicateError"
      class="user-chip-multi-select__duplicate-error"
      role="alert"
      aria-live="assertive"
      :data-testid="`${dataTestId}__duplicate-error`"
    >
      {{ duplicateError }}
    </p>
  </div>
</template>

<script>
import './UserChipMultiSelect.scss';
import { positionMenuNearActivator } from '@/utils/sync-attached-menu';
import { duplicateAttemptMessage } from '../../utils/bulkUploadValidationMessages';

const DUPLICATE_ERROR_TIMEOUT_MS = 4000;

/** Searchable multi-select with avatar chips for user assignment fields. */
export default {
  name: 'UserChipMultiSelect',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    /** User IDs omitted from the dropdown (caller-defined, e.g. mutual exclusion). */
    excludeIds: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Search users',
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'assignment-dialog', 'combobox', 'add-task'].includes(v),
    },
    attach: {
      type: Boolean,
      default: false,
    },
    currentUsersLabel: {
      type: String,
      default: '',
    },
    dataTestId: {
      type: String,
      default: 'user-chip-multi-select',
    },
    warnDuplicate: {
      type: Boolean,
      default: true,
    },
    roleLabel: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: 'No users found',
    },
    /** When set, add-task menu lists only these user ids (chips still resolve from `users`). */
    allowedMenuIds: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      searchQuery: null,
      selectionResetKey: 0,
      scrollCloseBinding: null,
      addTaskMenuOpen: false,
      menuSearchQuery: '',
      addTaskScrollCloseBinding: null,
      menuPinTimer: null,
      ignoreScrollClose: false,
      scrollIgnoreTimer: null,
      duplicateError: '',
      duplicateTimer: null,
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.bindScrollClose();
      this.bindAddTaskMenuScrollClose();
    });
  },

  updated() {
    this.$nextTick(() => {
      this.bindScrollClose();
      this.bindAddTaskMenuScrollClose();
    });
  },

  watch: {
    value(val) {
      if (!val || val.length === 0) {
        this.searchQuery = null;
      }
      this.refreshAddTaskMenuPosition();
    },
    addTaskMenuOpen(open) {
      if (open) {
        this.menuSearchQuery = '';
        this.suppressScrollClose();
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.pinAddTaskMenu();
            const input = this.$refs.addTaskMenuSearch?.$el?.querySelector('input');
            input?.focus();
            if (this.menuPinTimer) window.clearTimeout(this.menuPinTimer);
            this.menuPinTimer = window.setTimeout(() => {
              this.menuPinTimer = null;
              if (!this.addTaskMenuOpen) return;
              this.pinAddTaskMenu();
            }, 50);
          });
        });
      }
    },
    menuSearchQuery() {
      this.refreshAddTaskMenuPosition();
    },
    loading(isLoading) {
      if (isLoading && this.addTaskMenuOpen) {
        this.addTaskMenuOpen = false;
      }
    },
  },

  computed: {
    isCombobox() {
      return this.variant === 'combobox';
    },

    isComboboxEmpty() {
      return this.isCombobox && this.selectedUsers.length === 0;
    },

    comboboxPlaceholder() {
      if (!this.isComboboxEmpty) return '';
      const query = (this.searchQuery || '').trim();
      return query ? '' : this.placeholder;
    },

    comboboxMenuProps() {
      return {
        attach: this.attach,
        offsetY: true,
        maxHeight: 300,
        closeOnContentClick: true,
        contentClass: 'user-chip-multi-select__menu',
      };
    },

    comboboxFieldClass() {
      return 'user-chip-multi-select__combobox--inline';
    },

    normalizedValueIds() {
      const seen = new Set();
      return (this.value || [])
        .map((id) => this.normalizeId(id))
        .filter((id) => {
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
    },

    /** Users list with duplicate _id entries removed (keeps first occurrence). */
    uniqueUsers() {
      const seen = new Set();
      return (this.users || []).filter((user) => {
        const id = this.normalizeId(user._id);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },

    selectedUsers() {
      const ids = new Set(this.normalizedValueIds);
      return this.uniqueUsers.filter((user) => ids.has(this.normalizeId(user._id)));
    },

    /** Remount autocomplete when selection changes so stale search/selection state is dropped. */
    selectionKey() {
      return (this.value || []).join('|');
    },

    excludedIdSet() {
      return new Set((this.excludeIds || []).map((id) => this.normalizeId(id)).filter(Boolean));
    },

    allowedMenuIdSet() {
      if (!Array.isArray(this.allowedMenuIds) || !this.allowedMenuIds.length) return null;
      return new Set(
        this.allowedMenuIds.map((id) => this.normalizeId(id)).filter(Boolean),
      );
    },

    menuUsers() {
      const excluded = this.excludedIdSet;
      const allowedMenu = this.allowedMenuIdSet;
      const term = (this.menuSearchQuery || '').trim().toLowerCase();
      return this.uniqueUsers.filter((user) => {
        const id = this.normalizeId(user._id);
        if (allowedMenu && !allowedMenu.has(id)) return false;
        if (excluded.has(id)) return false;
        return this.userMatchesSearch(user, term);
      });
    },

    isAllMenuUsersSelected() {
      const visible = this.menuUsers;
      if (!visible.length) return false;
      return visible.every((user) => this.isUserSelected(user._id));
    },

    isAllMenuUsersIndeterminate() {
      const visible = this.menuUsers;
      if (!visible.length) return false;
      const selectedCount = visible.filter((user) => this.isUserSelected(user._id)).length;
      return selectedCount > 0 && selectedCount < visible.length;
    },

    /**
     * Dropdown options filtered by search and excludeIds.
     * Combobox keeps selected users in items (chips); hide-selected hides them in the menu.
     * Picker variants omit already-selected users from the list.
     */
    filteredUsers() {
      const excludeSelected = !this.isCombobox;
      const excluded = this.excludedIdSet;
      const selected = excludeSelected ? new Set(this.normalizedValueIds) : null;
      const term = (this.searchQuery || '').trim().toLowerCase();
      return this.uniqueUsers.filter((user) => {
        const id = this.normalizeId(user._id);
        if (excluded.has(id)) return false;
        if (selected?.has(id)) return false;
        return this.userMatchesSearch(user, term);
      });
    },
  },

  beforeDestroy() {
    this.unbindScrollClose();
    this.unbindAddTaskMenuScrollClose();
    if (this.menuPinTimer) {
      window.clearTimeout(this.menuPinTimer);
      this.menuPinTimer = null;
    }
    if (this.scrollIgnoreTimer) {
      window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = null;
    }
    this.searchQuery = null;
    this.clearDuplicateError();
  },

  methods: {
    isUserSelected(userId) {
      return this.normalizedValueIds.includes(this.normalizeId(userId));
    },

    toggleUser(userId) {
      const id = this.normalizeId(userId);
      const next = [...this.normalizedValueIds];
      const index = next.indexOf(id);
      if (index >= 0) {
        next.splice(index, 1);
        this.clearDuplicateError();
        this.$emit('input', next);
        return;
      }
      if (this.isDuplicateAdd(id)) {
        this.showDuplicateErrorForUserId(id);
        return;
      }
      next.push(id);
      this.clearDuplicateError();
      this.$emit('input', next);
    },

    toggleAllMenuUsers() {
      const visibleIds = this.menuUsers.map((user) => this.normalizeId(user._id));
      if (!visibleIds.length) return;

      const allSelected = visibleIds.every((id) => this.normalizedValueIds.includes(id));
      if (allSelected) {
        const visibleSet = new Set(visibleIds);
        this.clearDuplicateError();
        this.$emit('input', this.normalizedValueIds.filter((id) => !visibleSet.has(id)));
        return;
      }

      const next = [...this.normalizedValueIds];
      let duplicateId = '';
      visibleIds.forEach((id) => {
        if (next.includes(id)) return;
        if (this.isDuplicateAdd(id, next)) {
          if (!duplicateId) duplicateId = id;
          return;
        }
        next.push(id);
      });
      if (duplicateId) this.showDuplicateErrorForUserId(duplicateId);
      else this.clearDuplicateError();
      this.$emit('input', next);
    },

    clearAllUsers() {
      this.$emit('input', []);
      this.menuSearchQuery = '';
    },

    closeAddTaskMenu() {
      this.addTaskMenuOpen = false;
      this.menuSearchQuery = '';
    },

    refreshAddTaskMenuPosition() {
      if (this.variant !== 'add-task' || !this.addTaskMenuOpen) return;
      this.$nextTick(() => {
        this.pinAddTaskMenu();
        requestAnimationFrame(() => {
          if (!this.addTaskMenuOpen) return;
          this.pinAddTaskMenu();
        });
      });
    },

    pinAddTaskMenu() {
      if (this.variant !== 'add-task' || !this.addTaskMenuOpen) return;
      const activatorEl = this.$refs.addTaskActivator;
      if (!activatorEl) return;
      positionMenuNearActivator({
        activatorEl,
        menuClass: 'user-chip-multi-select__add-task-menu',
        fieldRef: this.$refs.addTaskMenu,
        maxMenuHeight: 320,
      });
    },

    onAddTaskActivatorClick(event, on) {
      if (this.loading) return;
      if (event?.target?.closest?.('.user-chip-multi-select__chip')) return;
      if (typeof on?.click === 'function') on.click(event);
    },

    suppressScrollClose(ms = 250) {
      this.ignoreScrollClose = true;
      if (this.scrollIgnoreTimer) window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = window.setTimeout(() => {
        this.ignoreScrollClose = false;
        this.scrollIgnoreTimer = null;
      }, ms);
    },

    bindAddTaskMenuScrollClose() {
      if (this.variant !== 'add-task' || this.addTaskScrollCloseBinding) return;
      const parent = this.findScrollParent(this.$el);
      if (!parent) return;
      const handler = () => {
        if (this.ignoreScrollClose) return;
        if (this.addTaskMenuOpen) this.closeAddTaskMenu();
      };
      parent.addEventListener('scroll', handler, { passive: true });
      this.addTaskScrollCloseBinding = { parent, handler };
    },

    unbindAddTaskMenuScrollClose() {
      if (!this.addTaskScrollCloseBinding) return;
      const { parent, handler } = this.addTaskScrollCloseBinding;
      parent.removeEventListener('scroll', handler);
      this.addTaskScrollCloseBinding = null;
    },

    findScrollParent(node) {
      let el = node?.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
        if (/(auto|scroll|overlay)/.test(overflow)) return el;
        el = el.parentElement;
      }
      return null;
    },

    bindScrollClose() {
      if (this.scrollCloseBinding) return;
      const parent = this.findScrollParent(this.$el);
      if (!parent) return;
      const handler = () => {
        const el = this.$refs.menuField;
        if (el?.isMenuActive) this.closeMenuField();
      };
      parent.addEventListener('scroll', handler, { passive: true });
      this.scrollCloseBinding = { parent, handler };
    },

    unbindScrollClose() {
      if (!this.scrollCloseBinding) return;
      const { parent, handler } = this.scrollCloseBinding;
      parent.removeEventListener('scroll', handler);
      this.scrollCloseBinding = null;
    },

    closeMenuField() {
      const el = this.$refs.menuField;
      if (!el) return;
      try {
        el.isMenuActive = false;
        if (el.$refs?.menu) el.$refs.menu.isActive = false;
        if (typeof el.blur === 'function') el.blur();
      } catch (_) {
        /* ignore */
      }
    },

    normalizeId(id) {
      if (id === undefined || id === null || id === '') return '';
      if (typeof id === 'object' && id._id != null) return String(id._id);
      return String(id);
    },

    sanitizeTestIdSegment(value, fallback = 'item') {
      const slug = String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      return slug || fallback;
    },

    getChipTestId(user, index) {
      const idSegment = this.sanitizeTestIdSegment(this.normalizeId(user?._id), 'user');
      if (index === undefined || index === null) {
        return `${this.dataTestId}__chip--${idSegment}`;
      }
      return `${this.dataTestId}__chip--${index}-${idSegment}`;
    },

    getMenuItemTestId(user, index) {
      const idSegment = this.sanitizeTestIdSegment(this.normalizeId(user?._id), 'user');
      return `${this.dataTestId}__menu-item--${index}-${idSegment}`;
    },

    userInitial(user) {
      const name = (user?.name || '').trim();
      return name ? name.charAt(0) : '?';
    },

    userMatchesSearch(user, term) {
      if (!term) return true;
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    },

    getUserById(userId) {
      const id = this.normalizeId(userId);
      return this.uniqueUsers.find((user) => this.normalizeId(user._id) === id) || null;
    },

    userEmailKey(user) {
      return String(user?.email || '').trim().toLowerCase();
    },

    isDuplicateAdd(userId, selectedIds = this.normalizedValueIds) {
      if (!this.warnDuplicate) {
        return selectedIds.includes(this.normalizeId(userId));
      }
      const id = this.normalizeId(userId);
      if (!id) return false;
      if (selectedIds.includes(id)) return true;
      const incomingEmail = this.userEmailKey(this.getUserById(id));
      if (!incomingEmail) return false;
      return (selectedIds || []).some((selectedId) => {
        const selectedEmail = this.userEmailKey(this.getUserById(selectedId));
        return selectedEmail && selectedEmail === incomingEmail;
      });
    },

    showDuplicateErrorForUserId(userId) {
      const user = this.getUserById(userId);
      const label = (user?.email || user?.name || '').trim();
      this.duplicateError = duplicateAttemptMessage(label, this.roleLabel);
      if (this.duplicateTimer) {
        window.clearTimeout(this.duplicateTimer);
      }
      this.duplicateTimer = window.setTimeout(() => {
        this.duplicateError = '';
        this.duplicateTimer = null;
      }, DUPLICATE_ERROR_TIMEOUT_MS);
    },

    clearDuplicateError() {
      if (this.duplicateTimer) {
        window.clearTimeout(this.duplicateTimer);
        this.duplicateTimer = null;
      }
      this.duplicateError = '';
    },

    resetSearchField() {
      this.searchQuery = null;
      this.selectionResetKey += 1;
    },

    onComboboxInput(selected) {
      const ids = (selected || [])
        .map((user) => this.normalizeId(user?._id ?? user))
        .filter(Boolean);
      const unique = [];
      let duplicateId = '';
      ids.forEach((id) => {
        if (unique.includes(id) || this.isDuplicateAdd(id, unique)) {
          if (!duplicateId) duplicateId = id;
          return;
        }
        unique.push(id);
      });
      if (duplicateId) this.showDuplicateErrorForUserId(duplicateId);
      else this.clearDuplicateError();
      this.$emit('input', unique);
      this.$nextTick(() => {
        this.searchQuery = null;
      });
    },

    onUserSelected(selected) {
      const userId = this.normalizeId(selected);
      if (!userId) {
        this.resetSearchField();
        return;
      }
      if (this.isDuplicateAdd(userId)) {
        this.showDuplicateErrorForUserId(userId);
        this.resetSearchField();
        return;
      }
      const next = [...this.normalizedValueIds];
      next.push(userId);
      this.clearDuplicateError();
      this.$emit('input', next);
      this.$nextTick(() => {
        this.searchQuery = null;
      });
    },

    removeUser(userId) {
      const target = this.normalizeId(userId);
      const next = this.normalizedValueIds.filter((id) => id !== target);
      this.clearDuplicateError();
      this.$emit('input', next);
      this.resetSearchField();
    },
  },
};
</script>
